<template>
  <a-auto-complete
    :value="value"
    :loading="loading"
    style="width: 100%"
    :options="options"
    @update:value="handleUpdate"
    @search="handleSearch"
  >
    <template #option="{ value: v, label: l }">
      <div class="opt-line opt-label">{{ l }}</div>
      <div class="opt-line opt-value">{{ v }}</div>
    </template>
    <template #default>
      <a-input-search
        :placeholder="placeholder"
        :value="value"
        size="middle"
        @search="handleSearch"
      >
        <template #suffix>
          <LoadingOutlined v-if="loading" />
          <span
            v-else
            :title="findItem(value)?.label"
            class="post-url-suffix"
          >
            {{ findItem(value)?.label }}
          </span>
        </template>
      </a-input-search>
    </template>
  </a-auto-complete>
</template>

<script setup lang="ts">
/* 数据源(postUrl)选择字段（port 自 FormDesignModal/BtnConfig/columnNameField.vue）：
   - a-auto-complete 变体：选项双行显示（第一行=数据源名称，第二行=URL）；
   - 输入框为 a-input-search，suffix 实时显示当前选中数据源的名称（非裸 URL）；
   - @search 输入本地过滤 value/label，同时 emit('search') 供父级做 >100 条时的远程过滤；
   - 选中项变化时 emit('select', item)（item 含 data 原始数据）供父级自动回填。 */
import { LoadingOutlined } from '@ant-design/icons-vue';
import { computed, ref } from 'vue';

const props = defineProps<{
  value: string;
  itemList: any[]; // [{ value: post_url, label: name, data: item }]
  placeholder?: string;
  loading?: boolean;
}>();
const emit = defineEmits<{
  (e: 'update:value', value: string): void;
  (e: 'select', item: any): void;
  (e: 'search', str: string): void;
}>();

/* ★ v29.22.1：options 由 itemList + 搜索词实时 computed 派生（此前是独立 ref，只在 @search 时
   重建——itemList 被父级远程搜索替换后 options 停在旧子集，且初始为空数组首开无选项）。 */
const searchText = ref('');
const lastSelected = ref<any>(null);
const findItem = (v: any) => props.itemList.find((item: any) => item.value === v) as any;

const options = computed(() =>
  props.itemList
    .filter(
      (item: any) =>
        !searchText.value ||
        item.value.includes(searchText.value) ||
        (item.label || '').includes(searchText.value),
    )
    .map((item: any) => ({ value: item.value, label: item.label })),
);

const handleSearch = (str: any) => {
  searchText.value = str || '';
  emit('search', str);
};

const handleUpdate = (v: any) => {
  emit('update:value', v);
  const currentSelected = findItem(v);
  if (currentSelected && currentSelected.value !== lastSelected.value) {
    emit('select', currentSelected);
    lastSelected.value = currentSelected.value;
    /* 选中后重置本地过滤词：重新打开下拉时展示完整列表（而非停在关键字子集） */
    searchText.value = '';
  }
};
</script>

<style scoped lang="less">
.opt-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &.opt-label {
    font-size: 14px;
    color: #2f2f2f;
  }
  &.opt-value {
    font-size: 12px;
    color: #999;
  }
}
.post-url-suffix {
  font-size: 12px;
  color: #999;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
